import { BaseElement } from 'base';
import { bus } from 'bus';
import { cart } from 'cart';
import { sectionFragment } from 'section-fragment';
import { isPlainClick } from 'is-plain-click';
import { SLIDE_CART_TOPIC, CART_DRAWER_ID } from 'slide-cart-topic';
import { SEARCH_TOPIC } from 'search-topic';
import { CART_COUNT_SECTION as COUNT_SECTION } from 'section-ids';

const COUNT_LABEL_ID = 'cart-count-label';
const LOCALE_TOPIC = 'locale-selector:active';
const MENU_TOPICS = ['mobile-menu:active', 'desktop-menu:active'];
const TOP_INSET_TOPIC = 'viewport:top-inset';
const TOP_INSET_PROPERTY = '--viewport-top-inset';
const SHOWN_INSET_PROPERTY = '--viewport-top-inset-shown';
const HIDDEN_INSET_PROPERTY = '--viewport-top-inset-hidden';
const SCROLL_THRESHOLD = 8;

class SiteHeader extends BaseElement {
  #inset = null;
  #shownInset = null;
  #hiddenInset = null;
  #lastY = null;
  #travel = 0;
  #frame = 0;
  #instantFrame = 0;
  #menus = new Set();

  onConnect() {
    this.cleanup(cart.watch(COUNT_SECTION));
    this.subscribe('cart:updated', this.renderCart);
    this.subscribe(LOCALE_TOPIC, this.renderLocale);
    this.listen(this, 'click', this.onClick);
    if (this.hasAttribute('data-auto-hide')) this.#bindAutoHide();
  }

  #bindAutoHide() {
    this.#inset = null;
    this.#shownInset = null;
    this.#hiddenInset = null;
    this.#lastY = null;
    this.#travel = 0;
    this.#menus = new Set();
    this.dataset.hidden = 'false';

    this.observe(new ResizeObserver(() => this.#publishInset()), this);
    this.listen(window, 'resize', this.#publishInset, { passive: true });
    this.cleanup(() => this.#releaseInset());
    this.#publishInset();
    if (window.Shopify?.designMode) return;

    MENU_TOPICS.forEach(topic =>
      this.subscribe(topic, value => this.#onMenu(topic, value))
    );
    this.listen(window, 'scroll', this.#onScroll, { passive: true });
    this.listen(window, 'pageshow', this.#onPageShow);
    this.listen(this, 'focusin', this.#onFocusIn);
    this.cleanup(() => {
      cancelAnimationFrame(this.#frame);
      cancelAnimationFrame(this.#instantFrame);
      this.#frame = 0;
      this.#instantFrame = 0;
    });
  }

  #onScroll() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#onFrame();
    });
  }

  #onFrame() {
    const max = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight
    );
    const y = Math.min(Math.max(window.scrollY, 0), max);
    const last = this.#lastY ?? y;
    this.#lastY = y;

    if (this.#forcedShown(y)) {
      this.#travel = 0;
      this.#setHidden(false);
      return;
    }

    const delta = y - last;
    if (!delta) return;
    if (Math.sign(delta) !== Math.sign(this.#travel)) this.#travel = 0;
    this.#travel += delta;
    if (Math.abs(this.#travel) <= SCROLL_THRESHOLD) return;
    this.#setHidden(this.#travel > 0);
  }

  #forcedShown(y) {
    return (
      y <= this.offsetTop + this.offsetHeight ||
      this.#menus.size > 0 ||
      this.#keyboardFocused()
    );
  }

  #keyboardFocused() {
    const active = document.activeElement;
    return Boolean(
      active && this.contains(active) && active.matches(':focus-visible')
    );
  }

  #onMenu(topic, value) {
    if (value) this.#menus.add(topic);
    else this.#menus.delete(topic);
    if (this.#menus.size > 0) this.#setHidden(false);
  }

  #onFocusIn() {
    if (this.dataset.hidden !== 'true' || !this.#keyboardFocused()) return;
    cancelAnimationFrame(this.#instantFrame);
    this.dataset.instant = '';
    this.#setHidden(false);
    this.#instantFrame = requestAnimationFrame(() => {
      this.#instantFrame = requestAnimationFrame(() => {
        this.#instantFrame = 0;
        delete this.dataset.instant;
      });
    });
  }

  #onPageShow() {
    this.#lastY = null;
    this.#travel = 0;
  }

  #setHidden(hidden) {
    const next = hidden ? 'true' : 'false';
    if (this.dataset.hidden === next) return;
    this.dataset.hidden = next;
    this.#publishInset();
  }

  #publishInset() {
    const root = document.documentElement.style;
    const shownInset = this.offsetTop + this.offsetHeight;
    if (shownInset !== this.#shownInset) {
      this.#shownInset = shownInset;
      root.setProperty(SHOWN_INSET_PROPERTY, `${shownInset}px`);
    }
    const hiddenInset = window.Shopify?.designMode
      ? shownInset
      : this.offsetTop;
    if (hiddenInset !== this.#hiddenInset) {
      this.#hiddenInset = hiddenInset;
      root.setProperty(HIDDEN_INSET_PROPERTY, `${hiddenInset}px`);
    }
    const next = this.dataset.hidden === 'true' ? this.offsetTop : shownInset;
    if (next === this.#inset) return;
    this.#inset = next;
    root.setProperty(TOP_INSET_PROPERTY, `${next}px`);
    bus.publish(TOP_INSET_TOPIC, next);
  }

  #releaseInset() {
    const root = document.documentElement.style;
    root.removeProperty(SHOWN_INSET_PROPERTY);
    root.removeProperty(HIDDEN_INSET_PROPERTY);
    root.removeProperty(TOP_INSET_PROPERTY);
    this.#shownInset = null;
    this.#hiddenInset = null;
    if (this.#inset === null) return;
    this.#inset = null;
    bus.publish(TOP_INSET_TOPIC, 0);
  }

  onClick(event) {
    const control = event.target.closest('[data-site-header-action]');
    if (!control || !this.contains(control)) return;
    if (control.getAttribute('aria-disabled') === 'true') return;

    switch (control.dataset.siteHeaderAction) {
      case 'search':
        bus.publish(SEARCH_TOPIC, true);
        break;
      case 'locale':
        bus.publish(LOCALE_TOPIC, control.getAttribute('aria-controls'));
        break;
      case 'cart':
        if (!isPlainClick(event)) return;
        event.preventDefault();
        bus.publish(SLIDE_CART_TOPIC, CART_DRAWER_ID);
        break;
    }
  }

  renderLocale(active) {
    this.qa('[data-site-header-action="locale"]').forEach(control =>
      control.setAttribute(
        'aria-expanded',
        active && control.getAttribute('aria-controls') === active
          ? 'true'
          : 'false'
      )
    );
  }

  renderCart({ sections } = {}) {
    const source = sectionFragment(sections, COUNT_SECTION, COUNT_LABEL_ID);
    if (!source) return;
    const label = source.textContent.trim();
    const count = Number.parseInt(source.dataset.count, 10);

    if (label)
      this.qa('[data-site-header-action="cart"]').forEach(control =>
        control.setAttribute('aria-label', label)
      );
    if (Number.isNaN(count)) return;
    this.qa(
      '[data-site-header-action="cart"] [data-site-header-count]'
    ).forEach(badge => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  }
}

if (!customElements.get('site-header'))
  customElements.define('site-header', SiteHeader);
